"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sourceRecordSchema = exports.patientLogicSchema = exports.measureLogicSchema = exports.trailRowSchema = exports.sourceRecordsQuerySchema = exports.patientLogicQuerySchema = exports.patientLogicParamsSchema = void 0;
// Wire shapes of a patient's measure logic and source records (CR-004, J-186; shadow plan section 7,
// Screen 4), served at `/api/v1/patients/:patientId/measure-logic` and `/source-records`. THESE CARRY
// PHI: the patient's name and date of birth, and the dates and codes of their visits. The patient in the
// URL is the opaque `patient_id` uuid (phi.md rule 2). Every label comes from the loaded bundle's
// `core.bundle_label`, falling back to the code itself. An other-ACO lead row carries only its date.
// J-260 (D8, the "why met / not met" pop-up; docs/aco-quality/why-modal/D8-SPEC.md, "Data"): the raw
// codes beside the labels, the A row's deciding facts, and what the pop-up's header and Rules list show.
const zod_1 = require("zod");
const inactive_js_1 = require("../quality-report/inactive.js");
exports.patientLogicParamsSchema = zod_1.z.object({ patientId: zod_1.z.uuid() });
// No `.meta({ id })` on a querystring schema: @fastify/swagger expands parameters inline.
exports.patientLogicQuerySchema = zod_1.z.object({
    year: zod_1.z.coerce.number().int().min(2000).max(2100),
});
exports.sourceRecordsQuerySchema = exports.patientLogicQuerySchema.extend({
    /** The B row's `activity_id`, as the trail gives it. */
    activityId: zod_1.z.string().min(1).max(200),
});
/** A code and its label from the bundle; the label is the code when the bundle names none. */
const labelled = zod_1.z.object({ code: zod_1.z.string(), label: zod_1.z.string() });
/** One B row of the trail. `otherAco` rows carry only `visitDate`; every other field is null. */
exports.trailRowSchema = zod_1.z
    .object({
    activityId: zod_1.z.string(),
    visitDate: zod_1.z.string(),
    otherAco: zod_1.z.boolean(),
    role: labelled.nullable(),
    effect: labelled.nullable(),
    evidenceCode: zod_1.z.string().nullable(),
    evidenceMeaning: zod_1.z.string().nullable(),
    rule: labelled.nullable(),
    decided: zod_1.z.boolean(),
    notCountedReason: labelled.nullable(),
    practiceName: zod_1.z.string().nullable(),
    practiceTin: zod_1.z.string().nullable(),
    renderingNpi: zod_1.z.string().nullable(),
    /** How many source records stand behind it; 0 for an other-ACO row. */
    records: zod_1.z.number().int(),
    /** J-260: `core.measure_activity.element_id`, the measure element the row is evidence of. */
    elementId: zod_1.z.string().nullable(),
    /** J-260: the raw `role`, `not_counted_reason` and `rule_id` beside their labels. */
    roleCode: zod_1.z.string().nullable(),
    notCountedReasonCode: zod_1.z.string().nullable(),
    ruleCode: zod_1.z.string().nullable(),
    /**
     * J-275 (QB3 item 13b): the visit's facility (the act line's `facility_name`, else its visit
     * record's); null when unknown, and for an other-ACO row.
     */
    facilityName: zod_1.z.string().nullable(),
    /**
     * J-301 (QB4 item 3c; docs/aco-quality/why-modal/CLOSING-RECORD-PROTOCOL.md): for a group or practice
     * caller, the record that decided the result when another ACO TIN produced it, labelled with that
     * TIN's name and performance group. Such a row carries only the measure's use of it, its date of
     * service and its code with the code's meaning; its practice, TIN, NPI, facility and source records
     * are withheld. Null on every other row.
     */
    recordedBy: zod_1.z
        .object({ tinName: zod_1.z.string().nullable(), performanceGroup: zod_1.z.string().nullable() })
        .nullable(),
})
    .meta({ id: "AcoTrailRow" });
/** One measure's A row and its trail. */
exports.measureLogicSchema = zod_1.z
    .object({
    acoCode: zod_1.z.string(),
    measureNumber: zod_1.z.string(),
    outcome: labelled,
    outcomeReason: labelled,
    gapOpen: zod_1.z.boolean(),
    gapStatus: labelled,
    gapAction: labelled.nullable(),
    decidingDate: zod_1.z.string().nullable(),
    /**
     * The A row's owner TIN and its practice name. J-301 (CLOSING-RECORD-PROTOCOL.md): null for a group or
     * practice caller when the owner is a TIN outside its own, which that caller sees only as "another ACO TIN".
     */
    ownerTin: zod_1.z.string().nullable(),
    practiceName: zod_1.z.string().nullable(),
    otherAcoLead: labelled.nullable(),
    trail: zod_1.z.array(exports.trailRowSchema),
    /** J-260: the A row's deciding QDC (`236`: both halves, `G8752+G8754`). */
    cmsQdc: zod_1.z.string().nullable(),
    exclusionElementId: zod_1.z.string().nullable(),
    exceptionElementId: zod_1.z.string().nullable(),
    ageAtAnchor: zod_1.z.number().int(),
    ageAnchorDate: zod_1.z.string(),
    qualifyingVisits: zod_1.z.number().int(),
    /** J-260: the raw `outcome_reason` beside `outcomeReason`'s label. */
    outcomeReasonCode: zod_1.z.string(),
    /** J-260: the portal's code of the row's ACO (`ref.aco.aco_code`); `acoCode` is Analysis's. */
    portalAcoCode: zod_1.z.string(),
    /**
     * J-260: the frame rules the pop-up's Rules list adds to the trail's own, with their bundle labels:
     * C-3 always, C-2 when the outcome is met or not met, C-4 for 001.
     */
    frameRules: zod_1.z.array(labelled),
    /**
     * J-274 (NR-05): the A row's person is inactive: Deceased (winning) or Discharged, and its date; null
     * when a date of neither is on file.
     */
    inactiveReason: zod_1.z.enum(inactive_js_1.INACTIVE_REASONS).nullable(),
    /** J-274: the reason in the configured words (`ui.yaml` `text`); null when active. */
    inactiveReasonLabel: zod_1.z.string().nullable(),
    inactiveDate: zod_1.z.string().nullable(),
    /**
     * J-274 (NR-05 c): the configured Action to Take of an inactive patient's open gap (`ui.yaml`
     * `text.inactive_action`); null when the patient is active or the gap is closed.
     */
    inactiveAction: zod_1.z.string().nullable(),
})
    .meta({ id: "AcoMeasureLogic" });
exports.patientLogicSchema = zod_1.z
    .object({
    patientId: zod_1.z.uuid(),
    firstName: zod_1.z.string(),
    lastName: zod_1.z.string(),
    dob: zod_1.z.string(),
    /** J-260: `core.patient.sex` (`female`, `male`), for the pop-up's header. */
    sex: zod_1.z.string().nullable(),
    /** J-260: the member id Screen 12 shows (the first non-HMX_PMI source id), or null. */
    memberId: zod_1.z.string().nullable(),
    performanceYear: zod_1.z.number().int(),
    /** The year's loaded bundle the rows are read from. */
    bundleId: zod_1.z.uuid(),
    /**
     * J-301 (QB4 item 9): the loaded bundle's as-of date (its manifest's `as_of_date`), the date the
     * pop-up's "Preliminary" lines name; null when the manifest carries none.
     */
    asOfDate: zod_1.z.string().nullable(),
    measures: zod_1.z.array(exports.measureLogicSchema),
})
    .meta({ id: "AcoPatientLogic" });
/** One `core.bundle_record` behind a B row, with its file. */
exports.sourceRecordSchema = zod_1.z
    .object({
    recordId: zod_1.z.string(),
    recordDate: zod_1.z.string().nullable(),
    tableName: zod_1.z.string(),
    code: zod_1.z.string().nullable(),
    codeSystem: zod_1.z.string().nullable(),
    meaning: zod_1.z.string().nullable(),
    npi: zod_1.z.string().nullable(),
    tin: zod_1.z.string().nullable(),
    practiceName: zod_1.z.string().nullable(),
    fileId: zod_1.z.string(),
    fileName: zod_1.z.string().nullable(),
    /** Where in the file: Analysis's `source_locator`, such as a sheet and row. */
    sourceLocator: zod_1.z.string(),
})
    .meta({ id: "AcoSourceRecord" });
//# sourceMappingURL=patient-logic.js.map